// Companies Frontend Functions Library
// These functions are self-contained and use _shared helpers directly (like Hoomie)

import {
  getDocumentById,
  getCollection,
  setDocById,
  addDocToCollection,
  updateDocById,
  deleteDocByCollectionAndId,
} from '../_shared/firestore/index.js';
import { CompanyModel } from '@cmms/models';
import { createCMMSView } from '../views/views.js';

const COLLECTION_NAME = 'companies';

// Frontend function: Get all companies
async function getCompanies(db) {
  try {
    const docs = await getCollection(db, COLLECTION_NAME);
    const companies = docs.map((doc) => {
      return { id: doc.$id, model: new CompanyModel(doc) };
    });
    return createCMMSView({ data: companies });
  } catch (error) {
    return createCMMSView({ error });
  }
}

// Frontend function: Get company by ID
async function getCompanyById(db, companyId) {
  try {
    const doc = await getDocumentById(db, COLLECTION_NAME, companyId);
    if (!doc) return createCMMSView({ error: 'Company not found' });
    const company = new CompanyModel(doc);
    return createCMMSView({ data: { id: doc.$id, model: company } });
  } catch (error) {
    return createCMMSView({ error });
  }
}

// Frontend function: Get company by email
async function getCompanyByEmail(db, email) {
  try {
    const docs = await getCollection(db, COLLECTION_NAME, {
      key: 'email_address',
      operator: '==',
      value: email,
    });

    if (docs.length === 0) {
      return createCMMSView({ error: 'Company not found' });
    }

    const company = docs[0];
    return createCMMSView({ data: { id: company.$id, model: new CompanyModel(company) } });
  } catch (error) {
    return createCMMSView({ error });
  }
}

// Frontend function: Create new company
async function createCompany(db, companyData) {
  try {
    const company = new CompanyModel(companyData);
    const docRef = await addDocToCollection(
      db,
      COLLECTION_NAME,
      company.toJSON()
    );

    return createCMMSView({ data: docRef });
  } catch (error) {
    return createCMMSView({ error });
  }
}

// Frontend function: Update company
async function updateCompany(db, companyId, updateData) {
  try {
    // First get the existing company data
    const existingCompany = await getDocumentById(db, COLLECTION_NAME, companyId);
    if (!existingCompany) {
      return createCMMSView({ error: 'Company not found' });
    }

    // Merge existing data with update data
    const mergedData = {
      ...existingCompany,
      ...updateData,
      updated_at: Date.now()
    };

    const company = new CompanyModel(mergedData);
    const docRef = await setDocById(
      db,
      COLLECTION_NAME,
      companyId,
      company.toJSON()
    );

    return createCMMSView({ data: { id: companyId, ...company.toJSON() } });
  } catch (error) {
    return createCMMSView({ error });
  }
}

// Frontend function: Delete company
async function deleteCompany(db, companyId) {
  try {
    await deleteDocByCollectionAndId(db, COLLECTION_NAME, companyId);
    return createCMMSView({ data: { id: companyId, deleted: true } });
  } catch (error) {
    return createCMMSView({ error });
  }
}

// Frontend function: Get companies by status
async function getCompaniesByStatus(db, status) {
  try {
    const docs = await getCollection(db, COLLECTION_NAME, {
      key: 'status',
      operator: '==',
      value: status,
    });

    const companies = docs.map((doc) => {
      return { id: doc.$id, model: new CompanyModel(doc) };
    });

    return createCMMSView({ data: companies });
  } catch (error) {
    return createCMMSView({ error });
  }
}

// Frontend function: Get companies by subscription plan
async function getCompaniesBySubscriptionPlan(db, subscriptionPlan) {
  try {
    const docs = await getCollection(db, COLLECTION_NAME, {
      key: 'subscription_plan',
      operator: '==',
      value: subscriptionPlan,
    });

    const companies = docs.map((doc) => {
      return { id: doc.$id, model: new CompanyModel(doc) };
    });

    return createCMMSView({ data: companies });
  } catch (error) {
    return createCMMSView({ error });
  }
}

// Frontend function: Get active companies
async function getActiveCompanies(db) {
  return await getCompaniesByStatus(db, 'Active');
}

// Frontend function: Get trial companies
async function getTrialCompanies(db) {
  return await getCompaniesByStatus(db, 'Trial');
}

// Frontend function: Get suspended companies
async function getSuspendedCompanies(db) {
  return await getCompaniesByStatus(db, 'Suspended');
}

// Frontend function: Get cancelled companies
async function getCancelledCompanies(db) {
  return await getCompaniesByStatus(db, 'Cancelled');
}

// Export frontend functions
export {
  getCompanies,
  getCompanyById,
  getCompanyByEmail,
  createCompany,
  updateCompany,
  deleteCompany,
  getCompaniesByStatus,
  getCompaniesBySubscriptionPlan,
  getActiveCompanies,
  getTrialCompanies,
  getSuspendedCompanies,
  getCancelledCompanies,
};
